import React, { useEffect, useMemo, useState } from 'react';
import { X, Banknote, CreditCard, Smartphone, Loader2, Receipt, CheckSquare, Square, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn, formatCurrency } from '../../lib/utils';
import { useTableOrderItems } from '../../lib/table-hooks';
import { useBillingInfo, processPayment, openPrintableInvoice, PaymentItemSnapshot } from '../../lib/billing-hooks';
import { TableWithOrder } from '../../lib/types';
interface PayTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  table: TableWithOrder | null;
  restaurantId: number | string | undefined;
  onSuccess: (allPaid: boolean) => void;
  createdBy?: string | null;
}
type PayMethod = 'cash' | 'sinpe' | 'card';
export function PayTableModal({
  isOpen,
  onClose,
  table,
  restaurantId,
  onSuccess,
  createdBy
}: PayTableModalProps) {
  const orderId = table?.current_order?.id;
  const {
    items,
    loading: itemsLoading,
    refetch
  } = useTableOrderItems(table?.id, restaurantId ? String(restaurantId) : undefined);
  const {
    info: billingInfo,
    loading: infoLoading
  } = useBillingInfo(restaurantId);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState<PayMethod>('cash');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Filter to unpaid items only
  const unpaidItems = useMemo(() => items.filter((i: any) => !i.paid_at), [items]);
  // Auto-select all when modal opens / items change
  useEffect(() => {
    if (isOpen && unpaidItems.length > 0) {
      setSelectedIds(new Set(unpaidItems.map(i => i.order_item_id)));
    }
    if (!isOpen) {
      setSelectedIds(new Set());
      setError(null);
      setMethod('cash');
    }
  }, [isOpen, unpaidItems.length]);
  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const allSelected = unpaidItems.length > 0 && selectedIds.size === unpaidItems.length;
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());else setSelectedIds(new Set(unpaidItems.map(i => i.order_item_id)));
  };
  const selectedItems = unpaidItems.filter(i => selectedIds.has(i.order_item_id));
  const total = selectedItems.reduce((s, i) => s + (i.line_total || 0), 0);
  const isPartial = selectedItems.length < unpaidItems.length;
  const handleSubmit = async () => {
    if (!orderId || !restaurantId || !billingInfo) {
      setError(!billingInfo ? 'Datos legales no configurados para este restaurante.' : 'Falta información de la mesa.');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Selecciona al menos un ítem para cobrar.');
      return;
    }
    setError(null);
    setSubmitting(true);
    const snapshot: PaymentItemSnapshot[] = selectedItems.map(i => ({
      order_item_id: i.order_item_id,
      item_name: i.item_name,
      quantity: i.quantity,
      unit_price: i.unit_price,
      line_total: i.line_total,
      notes: i.notes || null
    }));
    const result = await processPayment({
      restaurantId,
      orderId,
      tableId: table?.id || null,
      amount: total,
      method,
      items: snapshot,
      isPartial,
      createdBy: createdBy || null
    });
    setSubmitting(false);
    if (!result.success || !result.payment) {
      setError(result.error || 'Error al procesar el pago.');
      return;
    }
    // Open printable invoice
    openPrintableInvoice({
      info: billingInfo,
      payment: result.payment,
      tableLabel: table ? table.table_name || `Mesa ${table.table_number}` : null
    });
    // Refetch items to reflect paid status
    await refetch();
    const allPaidNow = !isPartial || selectedItems.length === unpaidItems.length;
    onSuccess(allPaidNow);
  };
  if (!isOpen || !table) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-id="element-745">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl" data-id="element-746">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-747">
          <div data-id="element-748">
            <h2 className="text-xl font-black text-gray-900 flex items-center gap-2" data-id="element-749">
              <Receipt className="w-5 h-5 text-amber-500" data-id="element-750" />
              Cobrar {table.table_name || `Mesa ${table.table_number}`}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-751">
              Selecciona los ítems a facturar (división de cuenta disponible)
            </p>
          </div>
          <button onClick={onClose} disabled={submitting} className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" data-id="element-752">
            <X className="w-5 h-5 text-gray-500" data-id="element-753" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-id="element-754">
          {infoLoading || itemsLoading ? <div className="flex justify-center py-12" data-id="element-755">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" data-id="element-756" />
            </div> : !billingInfo ? <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3" data-id="element-757">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" data-id="element-758" />
              <div className="text-sm text-red-700" data-id="element-759">
                No se encontraron datos legales para facturar. Ejecuta{' '}
                <code className="bg-red-100 px-1.5 py-0.5 rounded text-xs" data-id="element-760">
                  SETUP_BILLING_SYSTEM.sql
                </code>{' '}
                en Supabase.
              </div>
            </div> : unpaidItems.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-761">
              <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-3" data-id="element-762" />
              <p className="font-bold" data-id="element-763">No hay ítems pendientes de cobro</p>
              <p className="text-sm" data-id="element-764">
                Esta mesa ya fue facturada por completo.
              </p>
            </div> : <>
              {/* Select all */}
              <button onClick={toggleAll} className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-765">
                <span className="flex items-center gap-2 text-sm font-bold text-gray-900" data-id="element-766">
                  {allSelected ? <CheckSquare className="w-4 h-4 text-amber-500" data-id="element-767" /> : <Square className="w-4 h-4 text-gray-400" data-id="element-768" />}
                  {allSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
                </span>
                <span className="text-xs font-medium text-gray-500" data-id="element-769">
                  {selectedIds.size} / {unpaidItems.length} ítems
                </span>
              </button>

              {/* Items list */}
              <div className="space-y-1.5" data-id="element-770">
                {unpaidItems.map(item => {
              const isSelected = selectedIds.has(item.order_item_id);
              return <button key={item.order_item_id} onClick={() => toggleItem(item.order_item_id)} className={cn('w-full text-left flex items-start gap-3 px-3 py-3 rounded-xl border-2 transition-all', isSelected ? 'border-amber-400 bg-amber-50/50' : 'border-gray-100 hover:border-gray-200 bg-white')} data-id="element-771">
                      <div className="mt-0.5 shrink-0" data-id="element-772">
                        {isSelected ? <CheckSquare className="w-5 h-5 text-amber-500" data-id="element-773" /> : <Square className="w-5 h-5 text-gray-300" data-id="element-774" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-775">
                        <div className="flex items-baseline gap-2" data-id="element-776">
                          <span className="font-black text-gray-900 bg-gray-100 px-1.5 rounded text-xs" data-id="element-777">
                            {item.quantity}×
                          </span>
                          <p className="font-bold text-gray-900 text-sm truncate" data-id="element-778">
                            {item.item_name}
                          </p>
                        </div>
                        {item.notes && <p className="text-xs text-gray-500 italic mt-0.5" data-id="element-779">
                            {item.notes}
                          </p>}
                      </div>
                      <span className="font-bold text-gray-900 text-sm tabular-nums shrink-0" data-id="element-780">
                        {formatCurrency(item.line_total)}
                      </span>
                    </button>;
            })}
              </div>

              {/* Payment method */}
              <div className="pt-2" data-id="element-781">
                <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-782">
                  Método de pago
                </p>
                <div className="grid grid-cols-3 gap-2" data-id="element-783">
                  {([{
                id: 'cash',
                label: 'Efectivo',
                Icon: Banknote
              }, {
                id: 'sinpe',
                label: 'SINPE',
                Icon: Smartphone
              }, {
                id: 'card',
                label: 'Tarjeta',
                Icon: CreditCard
              }] as const).map(({
                id,
                label,
                Icon
              }) => <button key={id} onClick={() => setMethod(id)} className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all', method === id ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-784">
                      <Icon className="w-5 h-5 mb-1" data-id="element-785" />
                      <span className="text-xs font-bold" data-id="element-786">{label}</span>
                    </button>)}
                </div>
              </div>

              {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-start gap-2" data-id="element-787">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-788" />
                  {error}
                </div>}
            </>}
        </div>

        {/* Footer */}
        {billingInfo && unpaidItems.length > 0 && <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50 rounded-b-2xl" data-id="element-789">
            <div className="flex justify-between items-baseline mb-3" data-id="element-790">
              <div data-id="element-791">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-792">
                  {isPartial ? 'Total parcial' : 'Total a cobrar'}
                </p>
                {isPartial && <p className="text-[10px] text-amber-600 font-bold" data-id="element-793">
                    Cuenta dividida — quedará saldo pendiente
                  </p>}
              </div>
              <span className="text-3xl font-black text-amber-500 tabular-nums" data-id="element-794">
                {formatCurrency(total)}
              </span>
            </div>
            <Button fullWidth size="lg" onClick={handleSubmit} disabled={submitting || selectedItems.length === 0} className="bg-gray-900 hover:bg-gray-800 text-white font-bold shadow-lg" data-id="element-795">
              {submitting ? <span className="flex items-center justify-center gap-2" data-id="element-796">
                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-797" />
                  Procesando...
                </span> : <span className="flex items-center justify-center gap-2" data-id="element-798">
                  <Receipt className="w-5 h-5" data-id="element-799" />
                  Cobrar y Facturar
                </span>}
            </Button>
          </div>}
      </div>
    </div>;
}